import React, { useMemo } from 'react'
import { Autocomplete, Chip, TextField } from '@mui/material'

import useStoreRelationBrandSearch from '../../hooks/useStoreRelationBrandSearch'
import {
  AUTOCOMPLETE_POPPER_SLOT_PROPS,
  STORE_RELATION_FORM_TEXT
} from '../constants/storeRelationFormConstants'

const AssignmentBrandSelect = ({
  row,
  clientDbRef,
  currentBrandDbRefs,
  selectedBrandDbRefsInOtherRows,
  locallyAvailableBrandOptions,
  unavailableBrandDbRefs,
  onChange
}) => {
  const searchTerm = useMemo(() => {
    if (row.brandOptions?.length && row.brandSearch === '') return ''

    return row.brandSearch || ''
  }, [row.brandOptions, row.brandSearch])

  const { options, loading } = useStoreRelationBrandSearch({
    clientDbRef,
    salesmanDbRef: row.salesmanDbRef,
    searchTerm,
    currentBrandDbRefs
  })

  const filteredOptions = useMemo(() => {
    const blockedBrandRefs = new Set(selectedBrandDbRefsInOtherRows.filter(Boolean))
    const unavailableBrandRefsSet = new Set((unavailableBrandDbRefs || []).filter(Boolean))
    const normalizedSearchTerm = searchTerm.trim().toLowerCase()
    const localOptions = (locallyAvailableBrandOptions || []).filter((option) => {
      if (blockedBrandRefs.has(option?.db_ref)) return false
      if (unavailableBrandRefsSet.has(option?.db_ref)) return false
      if (!normalizedSearchTerm) return true

      const searchableText = [
        option?.label,
        option?.name,
        option?.db_ref
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(normalizedSearchTerm)
    })
    const mergedOptions = [...options]

    localOptions.forEach((option) => {
      const alreadyExists = mergedOptions.some((currentOption) => currentOption.id === option.id)

      if (!alreadyExists) {
        mergedOptions.push(option)
      }
    })

    return mergedOptions.filter((option) => {
      if (blockedBrandRefs.has(option?.db_ref)) return false
      if (unavailableBrandRefsSet.has(option?.db_ref)) return false

      return true
    })
  }, [
    locallyAvailableBrandOptions,
    options,
    searchTerm,
    selectedBrandDbRefsInOtherRows,
    unavailableBrandDbRefs
  ])

  return (
    <Autocomplete
      multiple
      fullWidth
      filterSelectedOptions
      className="store-relations-brand-autocomplete"
      slotProps={{ popper: AUTOCOMPLETE_POPPER_SLOT_PROPS }}
      options={filteredOptions}
      loading={loading}
      value={row.brandOptions || []}
      inputValue={row.brandSearch || ''}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      getOptionLabel={(option) => option?.label || ''}
      onChange={(_, nextOptions) => {
        onChange(row.id, nextOptions)
      }}
      onInputChange={(_, nextInputValue, reason) => {
        if (reason === 'reset') return

        onChange(row.id, row.brandOptions || [], nextInputValue)
      }}
      noOptionsText={STORE_RELATION_FORM_TEXT.NO_OPTIONS}
      loadingText={STORE_RELATION_FORM_TEXT.LOADING_BRANDS}
      renderTags={(value, getTagProps) => (
        value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.label}
            size="small"
            className="store-relations-brand-chip"
            sx={{
              mr: 0.5,
              mb: 0.5
            }}
          />
        ))
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={row.brandOptions?.length ? '' : STORE_RELATION_FORM_TEXT.BRAND_SEARCH_PLACEHOLDER}
        />
      )}
    />
  )
}

export default AssignmentBrandSelect
